import orderBy from 'lodash/orderBy'

import { addBookingFeeStore } from '@new/__global/components/AddBookingFee/stores/AddBookingFeeStore'
import { COUPON_DISCOUNT_TAX_FEE_ID } from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.constants'
import {
  BookingPriceFormProps,
  BookingPriceInputProps,
  TaxFeeAmountInput,
} from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.types'
import { travelAgentDiscountId } from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.utils'

import { PricingOverrides, TaxFeeOverride } from '@arriere/types/BookingPrices.types'

export const checkIsCreditCardFee = (taxFeeId?: string) => {
  const creditCardFeeId = '216a0414-132f-11ed-a24e-ffba3e848663'
  return taxFeeId?.includes(creditCardFeeId)
}

export const checkIsDiscountFee = (taxFeeId?: string) =>
  !!taxFeeId &&
  (taxFeeId === COUPON_DISCOUNT_TAX_FEE_ID || addBookingFeeStore.discountFeeIds.has(taxFeeId))

export const LARGE_DISCOUNT_THRESHOLD_PERCENT = 20

interface GetTotalPriceAdjustmentParams {
  originalSubtotal?: number
  newSubtotal?: number
}

export const getTotalPriceAdjustment = ({
  originalSubtotal,
  newSubtotal,
}: GetTotalPriceAdjustmentParams) => {
  const original = originalSubtotal ?? 0
  const adjustment = (newSubtotal ?? original) - original
  const adjustmentPercent = original ? Number(((adjustment / original) * 100).toFixed(2)) : 0

  return { adjustment, adjustmentPercent }
}

export const isLargeDiscountAdjustment = (adjustmentPercent: number) =>
  adjustmentPercent <= -LARGE_DISCOUNT_THRESHOLD_PERCENT

const roundToWholeDollars = (cents: number) => Math.round(cents / 100) * 100

export interface TotalPriceAdjustmentDetails {
  adjustment: number
  adjustmentPercent: number
  hasChangedTotal: boolean
  isIncrease: boolean
  isLargeDiscount: boolean
  recommendedTotal: number
}

// Derives everything the "Set total price" UI needs from the current (baseline) subtotal, the
// recalculated subtotal and the entered target. Baseline is the current member-inclusive total, so
// existing discounts stay in the baseline rather than the adjustment. Pure + flow-agnostic so other
// flows can reuse it by feeding their own values.
export const getTotalPriceAdjustmentDetails = ({
  currentSubtotal,
  recalculatedSubtotal,
  targetTotal,
}: {
  currentSubtotal: number
  recalculatedSubtotal?: number
  targetTotal?: number
}): TotalPriceAdjustmentDetails => {
  const { adjustment, adjustmentPercent } = getTotalPriceAdjustment({
    originalSubtotal: currentSubtotal,
    newSubtotal: recalculatedSubtotal,
  })
  const maxRecommendedDiscount = (currentSubtotal * LARGE_DISCOUNT_THRESHOLD_PERCENT) / 100

  return {
    adjustment,
    adjustmentPercent,
    hasChangedTotal: targetTotal != null && targetTotal !== currentSubtotal,
    isIncrease: adjustment > 0,
    isLargeDiscount: isLargeDiscountAdjustment(adjustmentPercent),
    recommendedTotal: roundToWholeDollars(currentSubtotal - maxRecommendedDiscount),
  }
}

interface ParseFormValuesToRecalculateProps {
  cachedTaxesAndFees?: TaxFeeOverride[]
  defaultTaxesAndFees?: TaxFeeAmountInput[]
  formValues: BookingPriceFormProps
  includeCreditCardFee?: boolean
  includeSecurityDeposit?: boolean
  taxExemption?: boolean
}

type TaxFeeRow = BookingPriceFormProps['taxesAndFees'][number]

type TaxFeeRowBeingEdited = Omit<TaxFeeRow, 'amount'> & { amount?: number }

interface TaxFeeOverrideProps {
  amount: number
  taxFeeId: string
}

/**
 * The tax and fee rows sent to repricing, without the card fee. A row with no amount is left out:
 * a fee just added with "Add fee" has none until the agent types it.
 */
export const getTaxFeesToReprice = (taxesAndFees: TaxFeeRowBeingEdited[]) =>
  taxesAndFees.filter(
    (fee): fee is TaxFeeRow => !checkIsCreditCardFee(fee.taxFeeId) && fee.amount != null,
  )

export const parseFormValuesToRecalculate = ({
  cachedTaxesAndFees,
  defaultTaxesAndFees,
  formValues,
  includeCreditCardFee,
  includeSecurityDeposit,
  taxExemption,
}: ParseFormValuesToRecalculateProps): PricingOverrides => {
  const taxesAndFees = getTaxFeesToReprice(formValues.taxesAndFees)

  const creditCardFee = !includeCreditCardFee
    ? formValues.taxesAndFees
        ?.filter(fee => checkIsCreditCardFee(fee.taxFeeId))
        .map(fee => ({ ...fee, amount: 0 }))
    : []

  const taxFeeOverrides = [...taxesAndFees, ...creditCardFee].reduce(
    (accTaxAndFeesOverrides: TaxFeeOverrideProps[], currentTaxFee) => {
      const cachedTaxFee = cachedTaxesAndFees?.find(
        cachedTaxFee => cachedTaxFee.taxFeeId === currentTaxFee.taxFeeId,
      )
      const defaultTaxFee = defaultTaxesAndFees?.find(
        defaultTaxFee => defaultTaxFee.taxFeeId === currentTaxFee.taxFeeId,
      )
      const hasMatchingPricingWithDefault = defaultTaxFee?.amount === currentTaxFee.amount

      if (cachedTaxFee && cachedTaxFee.amount === currentTaxFee.amount) {
        return [...accTaxAndFeesOverrides, cachedTaxFee]
      }

      return hasMatchingPricingWithDefault
        ? accTaxAndFeesOverrides
        : [
            ...accTaxAndFeesOverrides,
            {
              taxFeeId: currentTaxFee.taxFeeId || '',
              amount: currentTaxFee.amount,
            },
          ]
    },
    [],
  )

  return {
    taxExemption,
    nightsOverrides: formValues.nights
      .filter(nightItem => !Number.isNaN(nightItem?.price))
      .map(night => ({
        date: night.date,
        price: night?.price as number,
        discountApplied: night?.discountApplied,
      })),
    taxFeeOverrides,
    grossTaxFeeOverrides: formValues.grossTaxFeeOverrides
      ? formValues.grossTaxFeeOverrides
          ?.filter(grossOverride => grossOverride?.taxFeeId)
          ?.map(grossOverride => ({
            taxFeeId: grossOverride?.taxFeeId || '',
            amount: -grossOverride?.amount,
          }))
      : undefined,
    securityDeposit: includeSecurityDeposit ? formValues.deposits.security : 0,
  }
}

export const getTaxesAndFeesFields = (pricingInput?: BookingPriceInputProps) => {
  const creditCardFee = pricingInput?.taxesAndFees.find(taxItem =>
    checkIsCreditCardFee(taxItem.taxFeeId),
  )

  const taxesAndFeesValue = orderBy(
    pricingInput?.taxesAndFees
      ?.filter(tax => tax.taxFeeId !== travelAgentDiscountId)
      ?.filter(taxItem => !checkIsCreditCardFee(taxItem.taxFeeId)),
    'kind',
    'desc',
  )

  return creditCardFee ? taxesAndFeesValue.concat(creditCardFee) : taxesAndFeesValue
}
